import {
  BatchInferenceDto,
  BatchInferenceResponse,
  DeploymentImportDto,
  DeploymentUpdateDto,
  StartDeploymentResponse,
  DeploymentInferenceDto,
  ModelResponseDto,
  DeploymentEndStatusDto,
  DeploymentUploadStartDto,
  DeploymentEventSummary,
  FramesDTO,
  GetFramesDto,
  SearchForImageDto,
  ImageSearchResultDto,
  DeploymentResponseDto,
  MediaAssetListDto,
  MediaAssetSearchDto,
  MediaAssetQueryDto,
  MediaDetailResponseDto,
  PaginatedPredictionRunResponse,
} from "../components/common/ObjectTypes";
import {
  MediaAssetQueryType,
  MediaDetailRequestType,
  DeploymentSearchQueryType,
} from "../components/common/QueryTypes";
import axios from "./axios";
import { UnauthorizedError } from "./apiUtils";
import { getKeycloak } from "../keycloak";
import { isTokenRefreshError, refreshTokenBeforeRequest } from "./tokenRefresh";

export async function createDeployment(data: DeploymentUpdateDto) {
  try {
    const resp = await axios.post("/deployments", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getDeploymentByGuid(
  guid: string,
): Promise<DeploymentResponseDto> {
  try {
    const resp = await axios.get(`/deployments/guid/${guid}`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getDeployments(
  query: DeploymentSearchQueryType,
): Promise<{ deployments: DeploymentResponseDto[]; count: number }> {
  try {
    const resp = await axios.get("/deployments", {
      params: query,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getDeploymentDetails(deployment_id: number) {
  try {
    const resp = await axios.get(`/deployments/${deployment_id}`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function updateDeployment(deployment: DeploymentUpdateDto) {
  try {
    const resp = await axios.put(
      `/deployments/${deployment.deployment_id}`,
      deployment,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function importDeployment(data: DeploymentImportDto) {
  try {
    const resp = await axios.post("/deployments/import", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function inferenceDeployment(
  data: DeploymentInferenceDto,
  model?: ModelResponseDto,
) {
  try {
    // Using a consistent approach with detectDeployment, sending data directly
    const resp = await axios.post("/deployments/inference", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Registers an upload with the server and takes the deployment's upload lock. The Keycloak
 * token is refreshed first (AKL-891); a failed refresh rejects like any other failure and
 * the manager puts the upload in its stopped state.
 *
 * The post sets `suppressUnexpectedErrorRedirect` (AKL-823), as `endDeploymentUpload` does:
 * a 5xx must not hard-redirect the page to /unexpectedError, which threw the user's
 * selection away and left them no idea the upload never began. The caller receives the
 * interceptor's normalized `{ message, status, data }` for any 4xx or 5xx (the same shape
 * with no `status` after a network error or timeout, which the interceptor has already
 * toasted), its `UnauthorizedError` for a 401, or a `TokenRefreshError` from the pre-flight,
 * and says "Upload could not start" with the reason.
 * @param deployment_id the deployment being uploaded to
 * @param data the number of files selected
 */
export async function startDeploymentUpload(
  deployment_id: number,
  data: DeploymentUploadStartDto,
): Promise<StartDeploymentResponse> {
  try {
    await refreshTokenBeforeRequest();
    const resp = await axios.post(
      `/deployments/${deployment_id}/start_upload`,
      data,
      { suppressUnexpectedErrorRedirect: true },
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Records the end of an upload in one shot — the user's Cancel, and the release of a lock
 * granted after the page went away or the user cancelled. The Keycloak token is refreshed
 * first (AKL-891). For the retried COMPLETED/FAILED record see
 * `endDeploymentUploadWithRetry`.
 *
 * The post carries one narrow interceptor flag, `suppressUnexpectedErrorRedirect`: a 5xx must
 * not hard-redirect the page out from under the upload panel. A 403 — the server refusing
 * because another user's upload lock is live, the upload having been taken over while this
 * client was away (AKL-895) — needs no flag since AKL-939: the interceptor hands every 403
 * back normalized and never clears the token for one (it used to, which signed the user out
 * on the next request; AKL-895's per-call `suppressForbiddenHandling` was the workaround and
 * is gone). The broad `skipGlobalErrorHandler` is deliberately not used: a Cancel that cannot
 * reach the server should still get the interceptor's "Network Error" / timeout toast with
 * its Report this action. What the caller receives on failure, rethrown as is: the
 * interceptor's normalized `{ message, status, data }` for a 403 or 5xx (or any other 4xx);
 * the same shape with no `status` after a network error or timeout, which the interceptor
 * has already toasted; a `TokenRefreshError` when the pre-flight itself failed; and, for a
 * 401 — a token expired or revoked between the pre-flight and the post — the interceptor's
 * `UnauthorizedError`, the session-expiry flow every other request gets.
 * @param deployment_id the deployment whose upload ended
 * @param data the final status and file count
 */
export async function endDeploymentUpload(
  deployment_id: number,
  data: DeploymentEndStatusDto,
) {
  try {
    await refreshTokenBeforeRequest();
    const resp = await axios.post(
      `/deployments/${deployment_id}/end_upload`,
      data,
      { suppressUnexpectedErrorRedirect: true },
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/** How the final end_upload was resolved. Nothing axios-shaped escapes the helper. */
export type EndUploadOutcome =
  /** The server recorded the status. */
  | "recorded"
  /** The server answered 401: the session expired before the upload was recorded. */
  | "unauthorized"
  /**
   * The server answered 403: another user's upload lock is live — the upload was taken
   * over while this client was away (AKL-895). The files that were sent are on the
   * server; only the record is not this client's to write. Permanent, so never retried
   * and never re-sent as a beacon: while the other lock is live it would be refused
   * again, and once that lock went stale it could close the other user's run.
   */
  | "refused"
  /** The server answered with another error, or never answered within the schedule. */
  | "failed";

/**
 * Back-off between retries of the final end_upload, in ms: three retries, four attempts
 * in all. When attempts fail at once (no route to the server) a give-up comes within about
 * half a minute of the network returning; when they stall, within about two minutes — see
 * `END_UPLOAD_ATTEMPT_TIMEOUT_MS`.
 */
export const END_UPLOAD_RETRY_DELAYS_MS: readonly number[] = [
  2000, 5000, 15000,
];

/**
 * Per-attempt timeout for the final end_upload, in ms. Without it each attempt inherits
 * the shared client's timeout (`REACT_APP_CLIENT_TIMEOUT_MS`: 30 s in prod, 90 s on dev and
 * QA), so four stalled attempts could keep the user waiting over six minutes for the
 * give-up toast — bounded in attempts but not in time. 20 s is ample for a one-row status
 * write. The server may still process an attempt the client stopped waiting for; the
 * give-up toast's wording ("check this deployment's history later") allows for that.
 */
export const END_UPLOAD_ATTEMPT_TIMEOUT_MS = 20000;

/**
 * The bits of a raw axios rejection this module reads. Declared locally rather than
 * importing `AxiosError`: the `axios` package is ESM and the tests mock `./axios`, so
 * nothing here should pull the real package into the module graph (as feedbackApi.tsx).
 */
type RawAxiosRejection = { response?: { status?: number } };

const responseOf = (err: unknown): RawAxiosRejection["response"] =>
  typeof err === "object" && err !== null
    ? (err as RawAxiosRejection).response
    : undefined;

/** Resolves once the browser reports it is online — at once when it already is. */
const whenOnline = (): Promise<void> =>
  navigator.onLine === false
    ? new Promise((resolve) =>
        window.addEventListener("online", () => resolve(), { once: true }),
      )
    : Promise.resolve();

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Records the final status of an upload (COMPLETED or FAILED), retrying when the server
 * did not answer at all — the moments after a laptop wakes, when the network is not back
 * yet — so the outcome is not lost and the deployment's History does not sit at
 * "In Progress" until the server heals it hours later (AKL-890).
 *
 * Retry rule: only a rejection with no `response` (network error, client timeout) is
 * retried. Before each retry the helper waits for the window's `online` event when
 * `navigator.onLine` is false — that wait is deliberately unbounded, since retrying while
 * offline is pointless and the bound is the number of attempts — then the schedule's
 * back-off. Each attempt carries its own timeout (`END_UPLOAD_ATTEMPT_TIMEOUT_MS`) rather
 * than the shared client's, so once online the whole sequence ends within about two
 * minutes. A 401 is never retried: the token will not repair itself, and the caller must
 * tell the user. A 403 is never retried either and is its own outcome, `refused`
 * (AKL-895): the server refuses `end_upload` when another user's upload lock is live, so
 * the upload was taken over while this client was away — permanent, and the caller must
 * say so rather than report a fault. Any other answer from the server is not retried
 * either — it answered, so asking again is noise.
 *
 * Page exit while the helper runs: the manager marks the last file handled before it
 * reports completion, and the failure path resets the provider first, so by the time this
 * helper is called the upload panel shows nothing in progress and the provider's own
 * `pagehide` beacon would not fire. For as long as it runs — an attempt in flight, a wait
 * for `online`, a back-off — the helper therefore sends the same pending status as a
 * keep-alive beacon on `pagehide`: a tab closed or parked mid-retry still tells the server
 * COMPLETED or FAILED instead of leaving the upload "In Progress" for the server to heal
 * hours later. A page restored from the back/forward cache carries on retrying, so the
 * server may see the status twice; nothing more is sent once the helper has resolved —
 * after a refusal in particular, since a beacon would be refused again while the other
 * lock is live and could close the other user's run once it went stale.
 *
 * Every attempt sets `skipGlobalErrorHandler`, so a failed attempt raises no
 * "Network Error" toast, no hard redirect to /unexpectedError, and does not clear the
 * stored token; the caller surfaces the outcome instead. That is the broad flag
 * `docs/ARCHITECTURE_RECOMMENDATIONS.md` (item 5) says to avoid, used here for the reason
 * `getExport` and `sendFeedbackReport` chose it: a retried call needs the interceptor's
 * network-toast branch silenced, and no narrower flag does that. The failed request is
 * still recorded for the Report a problem dialog — the interceptor does that before it
 * honours the flag.
 *
 * Every attempt first refreshes the Keycloak token (AKL-891) — a retry after a two-minute
 * wait needs that check as much as the first attempt did. A refresh Keycloak refuses means
 * the session is over: the outcome is `unauthorized` at once, without posting (the same
 * message the user would get from a 401, and the page is already navigating to sign-in).
 * A refresh that got no answer is a no-response failure like any other: it waits for
 * `online`, backs off and tries again within the schedule.
 * @param deployment_id the deployment whose upload ended
 * @param data the final status and file count
 * @param retryDelaysMs the back-off schedule; a test seam — production callers take the default
 * @returns how the status was resolved (`recorded`, `unauthorized`, `refused` or `failed`);
 *   never throws for an HTTP failure
 */
export async function endDeploymentUploadWithRetry(
  deployment_id: number,
  data: DeploymentEndStatusDto,
  retryDelaysMs: readonly number[] = END_UPLOAD_RETRY_DELAYS_MS,
): Promise<EndUploadOutcome> {
  // While this helper waits — for an attempt to answer, for the network to come back, for
  // a back-off — the upload panel already shows nothing in progress, so the provider's own
  // pagehide beacon would not fire. Send the same pending status as a beacon instead.
  const sendPendingStatus = () =>
    endDeploymentUploadBeacon(deployment_id, data);
  window.addEventListener("pagehide", sendPendingStatus);
  try {
    for (let attempt = 0; ; attempt++) {
      try {
        await refreshTokenBeforeRequest();
        await axios.post(`/deployments/${deployment_id}/end_upload`, data, {
          skipGlobalErrorHandler: true,
          timeout: END_UPLOAD_ATTEMPT_TIMEOUT_MS,
        });
        return "recorded";
      } catch (err) {
        console.error(err);
        if (isTokenRefreshError(err)) {
          if (err.kind === "sessionEnded") {
            return "unauthorized";
          }
          // `unavailable`: Keycloak could not be reached — the same no-response failure
          // as a post that never arrived, handled by the wait and back-off below.
        } else {
          const response = responseOf(err);
          if (response?.status === 401) {
            return "unauthorized";
          }
          // Before the generic branch: a refusal is not a fault (AKL-895).
          if (response?.status === 403) {
            return "refused";
          }
          if (response !== undefined) {
            return "failed";
          }
        }
        if (attempt >= retryDelaysMs.length) {
          return "failed";
        }
        await whenOnline();
        await delay(retryDelaysMs[attempt]);
      }
    }
  } finally {
    window.removeEventListener("pagehide", sendPendingStatus);
  }
}

/**
 * Tells the server an upload ended because the page is going away (tab closed, reloaded
 * or navigated off), so the deployment's upload lock is released at once instead of hours
 * later. Fired from `pagehide` — Chrome removes `unload` for all sites at Chrome 154. A
 * regular axios request is not guaranteed to leave a page that is unloading, so this uses
 * the Fetch API with `keepalive`.
 *
 * The token is read from the Keycloak instance **here, when the event fires** — not handed
 * in by the caller. A string built at the provider's last render is stale after a long
 * sleep, and is literally `Bearer undefined` when Keycloak holds no token; both used to be
 * sent. With no token nothing is sent: an unauthenticated request cannot release the lock.
 * `authHeader()` is deliberately not used — with no token it clears storage and starts a
 * Keycloak logout, which must never be a side effect of closing a tab. Nor is the token
 * refreshed first (AKL-891): `pagehide` cannot await anything, so this is the one
 * upload-flow request that goes out with whatever token Keycloak holds.
 */
export function endDeploymentUploadBeacon(
  deployment_id: number,
  data: DeploymentEndStatusDto,
): void {
  const token = getKeycloak().token;
  if (!token) {
    console.warn(
      "No auth token available for endDeploymentUploadBeacon; nothing sent",
    );
    return;
  }
  const baseURL = axios.defaults.baseURL || "";
  const url = `${baseURL}/deployments/${deployment_id}/end_upload`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(data),
    keepalive: true,
  }).catch((err) => {
    // Can't do much here during page unload, but good for debugging
    console.error("Failed to send cleanup request:", err);
  });
}

/** How one upload heartbeat was resolved (AKL-896). Nothing axios-shaped escapes the call. */
export type UploadHeartbeatOutcome =
  /** The server refreshed the caller's lock: 200 `{"result": true}`. */
  | "accepted"
  /**
   * The server said this client cannot heartbeat: a 4xx other than 408 and 429 — 403 (the
   * caller is not the lock holder, or holds a lock the server already considers expired),
   * 400 (no such deployment), 404 (the endpoint is not deployed to this environment yet),
   * 401 (a token the server will not take). Asking again will not change the answer;
   * three of these in a row stop the schedule for the rest of the upload — see
   * `uploadHeartbeat.ts`.
   */
  | "refused"
  /**
   * A failure that may well pass: a 5xx (the server erred — a restart, a deploy), a 408 or
   * 429 (it asked for a pause), no answer at all (a network error, a timeout), or a token
   * refresh that could not reach Keycloak or that ended the session (Keycloak is then
   * already navigating to sign-in). Nothing to hold against the lock; the next beat asks
   * again. The split follows AKL-889's `classifyUploadError` for the blob requests.
   */
  | "transient";

/** The 4xx answers a heartbeat treats as transient rather than as a refusal; see the outcome type. */
const HEARTBEAT_TRANSIENT_4XX: ReadonlySet<number> = new Set([408, 429]);

/**
 * Tells the server this client is still uploading, so the deployment's upload lock does
 * not look abandoned (AKL-896). The lock's only liveness signal is `last_update_ms`, which
 * the browser otherwise refreshes only through each landed blob — so an upload between
 * files, paused, stopped or asleep looked idle, and after the steal window another user
 * could take its lock over. `POST /deployments/{id}/upload_heartbeat` takes no body,
 * refreshes the lock for the **current holder only**, records no History event, and
 * answers 403 to anyone else — including the holder once the lock is past
 * `UPLOAD_EXPIRED_THRESHOLD_HOURS`, whose run the server considers dead.
 *
 * The Keycloak token is refreshed first, through the same AKL-891 pre-flight every
 * upload-flow call makes. That pre-flight can be what discovers a dead session — a refresh
 * Keycloak refuses runs `clearToken()`, which fires `onAuthLogout` (the interrupted upload
 * is recorded) and navigates to sign-in — but that is a session that was already gone,
 * found by the same helper any other upload request would have used; the heartbeat itself
 * never clears a token or signs anyone out.
 *
 * A heartbeat is a background nicety and **no failure of it may disturb the upload**: this
 * function never throws, and the post carries the broad `skipGlobalErrorHandler` flag so
 * every branch of the interceptor stays silent — no token clear on a 401 or 403, no hard
 * redirect on a 5xx, and no "Network Error" / timeout toast, which no narrow flag silences
 * (the reason AKL-890's retrying helper chose the same flag; `docs/ARCHITECTURE_RECOMMENDATIONS.md`,
 * item 5). The interceptor still records a 5xx or no-response failure for the Report a
 * problem dialog before it honours the flag — a `sessionStorage` write and nothing more;
 * a 4xx is never recorded. Each failure is logged with `console.warn`, not `console.error`:
 * nothing about the upload went wrong.
 *
 * What counts as a refusal is only an answer that will not change if asked again — a 4xx
 * other than 408 and 429 (review cycle 1). A 5xx is the server erring, not the server
 * saying no: three of those during a restart would otherwise have stopped the schedule
 * for the rest of a long upload, and a paused or stopped upload would then have sent no
 * liveness signal once the server was back — its lock free to expire or be taken, which
 * is the failure this call exists to prevent.
 *
 * On a 403 the API's ticket comment suggests the client re-establish itself with
 * `start_upload`. The web deliberately does not do that from here: `start_upload` opens or
 * continues a History run, which is not a background nicety's decision to make, and the
 * web's takeover story is AKL-895's refused `end_upload`. Recorded as a candidate follow-up.
 * @param deployment_id the deployment whose upload lock this client holds
 * @returns how the server answered — `accepted`, `refused` or `transient`; never throws
 */
export async function heartbeatDeploymentUpload(
  deployment_id: number,
): Promise<UploadHeartbeatOutcome> {
  try {
    await refreshTokenBeforeRequest();
    await axios.post(
      `/deployments/${deployment_id}/upload_heartbeat`,
      undefined,
      {
        skipGlobalErrorHandler: true,
      },
    );
    return "accepted";
  } catch (err) {
    if (isTokenRefreshError(err)) {
      console.warn(
        `Upload heartbeat for deployment ${deployment_id} not sent: the token could not be refreshed (${err.kind})`,
      );
      return "transient";
    }
    const status = responseOf(err)?.status;
    if (status === undefined) {
      console.warn(
        `Upload heartbeat for deployment ${deployment_id} got no answer:`,
        err,
      );
      return "transient";
    }
    if (status >= 400 && status < 500 && !HEARTBEAT_TRANSIENT_4XX.has(status)) {
      console.warn(
        `Upload heartbeat for deployment ${deployment_id} was refused (${status})`,
      );
      return "refused";
    }
    console.warn(
      `Upload heartbeat for deployment ${deployment_id} failed for now (${status}); the next one asks again`,
    );
    return "transient";
  }
}

export async function detectDeployment(data: DeploymentInferenceDto) {
  try {
    const resp = await axios.post("/deployments/detection", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function classifyDeployment(data: DeploymentInferenceDto) {
  try {
    const resp = await axios.post("/deployments/classification", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function batchDetectDeployments(
  data: BatchInferenceDto,
): Promise<BatchInferenceResponse> {
  try {
    const resp = await axios.post("/deployments/project_detection", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function batchClassifyDeployments(
  data: BatchInferenceDto,
): Promise<BatchInferenceResponse> {
  try {
    const resp = await axios.post("/deployments/project_classification", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function retryPredictionRun(
  deployment_id: number,
  run_id: number,
): Promise<{ prediction_run_id: number }> {
  try {
    const resp = await axios.post(
      `/deployments/${deployment_id}/prediction_runs/${run_id}/retry`,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getDeploymentEventHistory(deployment_id: number) {
  try {
    const resp = await axios.get(`/deployments/${deployment_id}/events`);
    return resp.data || [];
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getLatestDeploymentEvent(
  deployment_id: number,
): Promise<DeploymentEventSummary | null> {
  try {
    const resp = await axios.get(`/deployments/${deployment_id}/latest_event`);
    return resp.data || null;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getDeploymentPredictionRuns(
  deployment_id: number,
  limit = 10,
  offset = 0,
  sort_by = "created_at",
  sort_direction: "asc" | "desc" = "desc",
): Promise<PaginatedPredictionRunResponse> {
  try {
    const resp = await axios.get(
      `/deployments/${deployment_id}/prediction_runs`,
      { params: { limit, offset, sort_by, sort_direction } },
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getDeploymentHasActivePredictionRun(
  deployment_id: number,
): Promise<boolean> {
  try {
    const resp = await axios.get(
      `/deployments/${deployment_id}/prediction_runs/has_active`,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getDeploymentHasActiveImportOrUpload(
  deployment_id: number,
): Promise<boolean> {
  try {
    const resp = await axios.get(
      `/deployments/${deployment_id}/has_active_import_or_upload`,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function checkIfUploadExpired(deployment_id: number) {
  try {
    const resp = await axios.get(
      `/deployments/${deployment_id}/check_if_upload_expired`,
    );
    return resp.data || false;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getFrames(data: GetFramesDto): Promise<FramesDTO> {
  try {
    const resp = await axios.post(`/deployments/get_frames`, data);
    return resp.data || [];
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getFrameCount(data: {
  deployment_ids: number[];
  project_id: number;
}) {
  try {
    const resp = await axios.post(`/deployments/frame_count`, data);
    return resp.data?.count || 0;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function searchForImage(
  data: SearchForImageDto,
): Promise<ImageSearchResultDto> {
  try {
    const resp = await axios.post(`/deployments/search_for_image`, data);
    return resp.data || {};
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getMediaAssets(
  projectId: number,
  deploymentIds: number[] | null,
  skip: number,
  limit: number,
): Promise<MediaAssetListDto> {
  try {
    const resp = await axios.post("/media-assets/review", {
      project_id: projectId,
      deployment_ids: deploymentIds,
      skip,
      limit,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Query media assets for the review feed (AKL-680). Composes the full §6.1.2
 * filter set, a sort choice, offset pagination, and a counts payload.
 * @param query the media query request (project_id, filters, sort, skip, limit)
 * @returns the matching page of media assets plus total and counts
 */
export async function queryMediaAssets(
  query: MediaAssetQueryType,
): Promise<MediaAssetQueryDto> {
  try {
    const resp = await axios.post("/media-assets/query", query);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Fetch the read-only single-photo review detail for one media asset (AKL-681).
 * Returns the resolved per-prediction overlay data, aggregated suggestion
 * cards, existing observations, media metadata, and — when a navigation
 * context (filters + sort) is supplied — the server-computed prev/next and
 * progress position over the same filtered ordering as the feed.
 * @param request the detail request (media_id + optional navigation context)
 * @returns the composed media detail payload
 */
export async function getMediaDetail(
  request: MediaDetailRequestType,
): Promise<MediaDetailResponseDto> {
  try {
    const resp = await axios.post("/media-assets/detail", request);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function searchMediaAssets(
  projectId: number,
  deploymentIds: number[],
  fileName: string,
  pageSize: number,
): Promise<MediaAssetSearchDto> {
  try {
    const resp = await axios.post("/media-assets/search", {
      project_id: projectId,
      deployment_ids: deploymentIds,
      file_name: fileName,
      page_size: pageSize,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * How Media Management's "End upload" call finished (AKL-897). Named apart from
 * `EndUploadOutcome` above, which is the upload machinery's own end-of-run result
 * (AKL-890): the two calls answer different questions and share only "failed" and
 * "refused".
 */
export type EndUploadRunOutcome =
  /** The server recorded the end of the run. */
  | "ended"
  /** The server answered 403: another user's upload lock is live (AKL-895). */
  | "refused"
  /** The server answered 401: the session is over, and the token has been cleared. */
  | "unauthorized"
  /** The server answered with another error, or never answered. */
  | "failed";

/**
 * End a deployment's open upload run from Media Management (AKL-897) — the action for a lock
 * whose client died — and say how it went rather than throwing.
 *
 * Carries the one narrow interceptor flag `endDeploymentUpload` above also uses,
 * `suppressUnexpectedErrorRedirect`: a 5xx here must reach the dialog rather than hard-redirect
 * the page out from under an open confirm. Under AKL-895 a 403 is exactly how the server
 * *refuses* this call (someone else's lock is still live), and that refusal has to reach the
 * dialog too — it does, with nothing to say to the interceptor: since AKL-939 every 403 is
 * handed back normalized with the token untouched (AKL-895's per-call
 * `suppressForbiddenHandling` was the workaround for the old rule and is gone).
 *
 * Not the broad `skipGlobalErrorHandler` (which this call used until review cycle 4): that flag
 * also swallows the 401 session-expiry flow — an expired token was reported to the user as a
 * retryable upload failure, and every retry failed the same way — and silences the interceptor's
 * "Network Error" / "Server timeout error" toast with its Report this action, which a page action
 * that cannot reach the server should still raise. The narrow flag is what architecture
 * recommendation 5 asks for.
 *
 * What the catch sees, therefore, is the interceptor's *normalized* `{ message, status, data }`
 * — a top-level `status`, not `err.response.status` — for a 403, any other 4xx and a 5xx; the
 * same shape with no `status` after a network error or timeout, which the interceptor has
 * already toasted; and an `UnauthorizedError` for a 401, the token by then already cleared.
 *
 * The upload machinery's own end-of-run calls keep using `endDeploymentUpload`; this one is
 * for the page action only.
 *
 * Like every other upload-flow call it refreshes the Keycloak token first (AKL-891 AC2): a
 * page left open for hours would otherwise post with a token that expired while the refresh
 * timer slept. A pre-flight that finds the session ended is `unauthorized` (the page's
 * session-expiry route); one that could not reach the sign-in service is `failed`, where
 * "Please try again" is the right answer. Nothing is posted in either case.
 * @param deployment_id the deployment whose open run is to be ended
 * @param data the terminal status (`failed` from the page action) and the last known count
 * @returns `ended` on a 2xx; `refused` on a 403; `unauthorized` on a 401 or an ended session;
 *   `failed` on anything else (a 5xx — today's server wraps every error as one — a 400, a
 *   network error, a timeout, a sign-in service that did not answer)
 */
export async function endDeploymentUploadRun(
  deployment_id: number,
  data: DeploymentEndStatusDto,
): Promise<EndUploadRunOutcome> {
  try {
    await refreshTokenBeforeRequest();
    await axios.post(`/deployments/${deployment_id}/end_upload`, data, {
      suppressUnexpectedErrorRedirect: true,
    });
    return "ended";
  } catch (err) {
    console.error(err);
    if (err instanceof UnauthorizedError) return "unauthorized";
    if (isTokenRefreshError(err)) {
      return err.kind === "sessionEnded" ? "unauthorized" : "failed";
    }
    // Narrowed by shape rather than `axios.isAxiosError`: importing the `axios` package here
    // would pull the untransformed ESM module into every test that imports this file. The
    // shape is the interceptor's normalized error, whose `status` is top-level.
    const status = (err as { status?: number } | null)?.status;
    return status === 403 ? "refused" : "failed";
  }
}
